import { useMemo } from 'react'
import { ArrowLeftIcon, ArrowRightIcon, FileText } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { Badge } from '@klear/ui/components/badge'
import { Skeleton } from '@klear/ui/components/skeleton'
import { useKlearAccountTransactions } from '@/hooks/use-klear-account-transactions'
import { DEFAULT_FINANCIAL_HISTORY_WINDOW_RANGE } from '@klear/constants'
import { cn } from '@klear/ui/lib/utils'
import times from 'lodash/times'

interface RecentTransactionsProps {
  historyWindowDays?: number
}

export function RecentTransactions({
  historyWindowDays = DEFAULT_FINANCIAL_HISTORY_WINDOW_RANGE
}: RecentTransactionsProps) {
  const { transactions, isLoading } = useKlearAccountTransactions(historyWindowDays)

  const orderedTransactions = useMemo(() => {
    const pending = transactions.filter((t) => !t.isSettled)
    const completed = transactions.filter((t) => t.isSettled)
    return [...pending, ...completed]
  }, [transactions])

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle>Recent Transactions</CardTitle>
      </CardHeader>
      <CardContent className="p-6 pt-0">
        <div className="space-y-8">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b text-sm text-muted-foreground">
                  <th className="pb-2 text-left font-medium">Date</th>
                  <th className="pb-2 text-left font-medium">Status</th>
                  <th className="pb-2 text-left font-medium">Type</th>
                  <th className="pb-2 text-left font-medium">Description</th>
                  <th className="pb-2 text-right font-medium pr-6">Amount</th>
                </tr>
              </thead>
              <tbody>
                {isLoading
                  ? times(5, (index) => (
                      <tr key={`skeleton-${index}`} className="border-b last:border-0">
                        <td className="py-3">
                          <Skeleton className="h-4 w-20" />
                        </td>
                        <td className="py-3">
                          <Skeleton className="h-6 w-16" />
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <Skeleton className="h-4 w-4" />
                            <Skeleton className="h-4 w-16" />
                          </div>
                        </td>
                        <td className="py-3">
                          <Skeleton className="h-4 w-32" />
                        </td>
                        <td className="py-3 text-right">
                          <Skeleton className="h-4 w-20 ml-auto" />
                        </td>
                      </tr>
                    ))
                  : orderedTransactions.map((transaction) => (
                      <tr key={transaction.id} className="border-b last:border-0">
                        <td className="py-3 text-sm">{transaction.createdAt.toKlearDate().toString()}</td>
                        <td className="py-3">
                          <div className="flex flex-col justify-center gap-0.5">
                            <Badge variant="outline" className="w-fit mt-1">
                              {transaction.status}
                            </Badge>

                            {transaction.failureReason ? (
                              <p className="pl-1.5 text-[10px] text-muted-foreground">{transaction.failureReason}</p>
                            ) : null}

                            {!transaction.isSettled && transaction.expectedArrivalAt ? (
                              <p className="pl-1.5 text-[10px]">
                                Expected Arrival: {transaction.expectedArrivalAt.add(1, 'day').toKlearDate().toString()}
                              </p>
                            ) : null}
                          </div>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-2 text-sm">
                            {transaction.isOutbound ? (
                              <ArrowRightIcon className="h-4 w-4" />
                            ) : (
                              <ArrowLeftIcon className="h-4 w-4" />
                            )}
                            {transaction.typeLabel}
                          </div>
                        </td>
                        <td className="py-3 text-sm">{transaction.description}</td>
                        <td
                          className={cn(
                            'py-3 font-medium text-right',
                            transaction.isFailed && 'text-muted-foreground',
                            !transaction.isFailed && transaction.isOutbound && 'text-red-600',
                            !transaction.isFailed && !transaction.isOutbound && 'text-green-600'
                          )}
                        >
                          <div className="flex gap-1 items-center justify-end">
                            {transaction.amount.toString()}
                            <div className="w-8 h-8 flex items-center justify-center">
                              {transaction.receiptUrl && (
                                <a
                                  href={transaction.receiptUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-muted transition-colors"
                                  title="View receipt"
                                >
                                  <FileText className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                </a>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
